import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  Table,
  Badge,
  Spinner
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FaFileExport, FaCalendar, FaChartBar, FaDownload } from 'react-icons/fa';
import { reglementActions } from '../../sagas/reglementSlice';
import { formatPrice } from '../../helpers/utility';
import * as XLSX from 'xlsx';

const ReglementReport = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { list, loading } = useSelector(state => state.reglements);
  const { clients } = useSelector(state => state.clients);
  
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [modeReglementFilter, setModeReglementFilter] = useState('');
  const [reportData, setReportData] = useState([]);

  useEffect(() => {
    // Initialiser avec le mois courant
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setDateDebut(firstDay.toISOString().split('T')[0]);
    setDateFin(lastDay.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (dateDebut && dateFin) {
      generateReport();
    }
  }, [dateDebut, dateFin, clientFilter, statutFilter, modeReglementFilter, list]);

  const generateReport = () => {
    let filtered = list??[].filter(reglement => {
      const dateReglement = new Date(reglement.dateReglement);
      const debut = new Date(dateDebut);
      const fin = new Date(dateFin);
      
      const inDateRange = dateReglement >= debut && dateReglement <= fin;
      const matchesClient = !clientFilter || reglement.clientId.toString() === clientFilter;
      const matchesStatut = !statutFilter || reglement.statut === statutFilter;
      const matchesMode = !modeReglementFilter || reglement.modeReglement === modeReglementFilter;
      
      return inDateRange && matchesClient && matchesStatut && matchesMode;
    });

    setReportData(filtered);
  };

  const exportToExcel = () => {
    const exportData = reportData.map(reglement => {
      const client = clients.find(c => c.id === reglement.clientId);
      return {
        'Référence': reglement.reference || reglement.id,
        'Client': client ? `${client.nom} ${client.prenom || ''}`.trim() : 'N/A',
        'Date Règlement': new Date(reglement.dateReglement).toLocaleDateString('fr-FR'),
        'Montant Réglé': reglement.montantRegle,
        'Mode Règlement': t(`reglement.${reglement.modeReglement.toLowerCase()}`),
        'Statut': t(`reglement.${reglement.statut.toLowerCase()}`),
        'Référence Paiement': reglement.referencePaiement || '',
        'Banque': reglement.banque || '',
        'Date Encaissement': reglement.dateEncaissement 
          ? new Date(reglement.dateEncaissement).toLocaleDateString('fr-FR') 
          : '',
        'Factures': reglement.numFactures || '',
        'Bons Livraison': reglement.numBonLivraisons || '',
        'Commentaire': reglement.commentaire || ''
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Règlements');
    
    const filename = `reglements_${dateDebut}_${dateFin}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  // Calculs statistiques
  const totalMontant = reportData.reduce((sum, r) => sum + (r.montantRegle || 0), 0);
  const totalEncaisse = reportData
    .filter(r => r.statut === 'ENCAISSE')
    .reduce((sum, r) => sum + (r.montantRegle || 0), 0);
  const totalEnAttente = reportData
    .filter(r => r.statut === 'EN_ATTENTE')
    .reduce((sum, r) => sum + (r.montantRegle || 0), 0);

  // Répartition par mode de règlement
  const repartitionModes = reportData.reduce((acc, reglement) => {
    const mode = reglement.modeReglement;
    if (!acc[mode]) {
      acc[mode] = { count: 0, montant: 0 };
    }
    acc[mode].count++;
    acc[mode].montant += reglement.montantRegle || 0;
    return acc;
  }, {});

  // Répartition par statut
  const repartitionStatuts = reportData.reduce((acc, reglement) => {
    const statut = reglement.statut;
    if (!acc[statut]) {
      acc[statut] = { count: 0, montant: 0 };
    }
    acc[statut].count++;
    acc[statut].montant += reglement.montantRegle || 0;
    return acc;
  }, {});

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <CardBody className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 d-flex align-items-center">
                <FaChartBar className="text-primary me-2" />
                {t('reglement.rapport')}
              </h4>
              <Button color="success" onClick={exportToExcel} disabled={reportData.length === 0}>
                <FaDownload className="me-2" />
                {t('common.exportExcel')}
              </Button>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Filtres */}
      <Card className="shadow-sm mb-4">
        <CardHeader>
          <h5 className="mb-0">
            <FaCalendar className="me-2" />
            {t('reglement.filtres')}
          </h5>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md={3}>
              <label className="form-label">{t('reglement.dateDebut')}</label>
              <Input
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <label className="form-label">{t('reglement.dateFin')}</label>
              <Input
                type="date"
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <label className="form-label">{t('reglement.client')}</label>
              <Input
                type="select"
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
              >
                <option value="">{t('reglement.allClients')}</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {`${client.nom} ${client.prenom || ''}`.trim()}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={2}>
              <label className="form-label">{t('reglement.statut')}</label>
              <Input
                type="select"
                value={statutFilter}
                onChange={(e) => setStatutFilter(e.target.value)}
              >
                <option value="">{t('reglement.allStatuts')}</option>
                <option value="EN_ATTENTE">{t('reglement.en_attente')}</option>
                <option value="VALIDE">{t('reglement.valide')}</option>
                <option value="ENCAISSE">{t('reglement.encaisse')}</option>
                <option value="REJETE">{t('reglement.rejete')}</option>
                <option value="ANNULE">{t('reglement.annule')}</option>
              </Input>
            </Col>
            <Col md={2}>
              <label className="form-label">{t('reglement.modeReglement')}</label>
              <Input
                type="select"
                value={modeReglementFilter}
                onChange={(e) => setModeReglementFilter(e.target.value)}
              >
                <option value="">{t('reglement.allModes')}</option>
                <option value="ESPECES">{t('reglement.especes')}</option>
                <option value="CHEQUE">{t('reglement.cheque')}</option>
                <option value="VIREMENT">{t('reglement.virement')}</option>
                <option value="CARTE_BANCAIRE">{t('reglement.carteBancaire')}</option>
                <option value="TRAITE">{t('reglement.traite')}</option>
                <option value="AUTRE">{t('reglement.autre')}</option>
              </Input>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Statistiques globales */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="shadow-sm bg-gradient h-100">
            <CardBody className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                    {t('reglement.totalReglements')}
                  </div>
                  <div className="h3 mb-0 font-weight-bold">{reportData.length}</div>
                  <div className="text-xs text-primary mt-2">
                    {formatPrice(totalMontant)}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm bg-gradient h-100">
            <CardBody className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                    {t('reglement.totalEncaisse')}
                  </div>
                  <div className="h3 mb-0 font-weight-bold text-success">
                    {formatPrice(totalEncaisse)}
                  </div>
                  <div className="text-xs text-success mt-2">
                    {totalMontant > 0 ? Math.round((totalEncaisse / totalMontant) * 100) : 0}%
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm bg-gradient h-100">
            <CardBody className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                    {t('reglement.totalEnAttente')}
                  </div>
                  <div className="h3 mb-0 font-weight-bold text-warning">
                    {formatPrice(totalEnAttente)}
                  </div>
                  <div className="text-xs text-warning mt-2">
                    {totalMontant > 0 ? Math.round((totalEnAttente / totalMontant) * 100) : 0}%
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm bg-gradient h-100">
            <CardBody className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                    {t('reglement.montantMoyen')}
                  </div>
                  <div className="h3 mb-0 font-weight-bold text-info">
                    {formatPrice(reportData.length > 0 ? totalMontant / reportData.length : 0)}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Répartition par mode de règlement */}
        <Col md={6}>
          <Card className="shadow-sm mb-4">
            <CardHeader>
              <h5 className="mb-0">{t('reglement.repartitionModes')}</h5>
            </CardHeader>
            <CardBody>
              <Table striped size="sm">
                <thead>
                  <tr>
                                        <th>{t('reglement.modeReglement')}</th>
                    <th className="text-center">{t('common.count')}</th>
                    <th className="text-end">{t('reglement.montantTotal')}</th>
                    <th className="text-center">%</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(repartitionModes).map(([mode, data]) => (
                    <tr key={mode}>
                      <td>
                        <span className="me-2">
                          {mode === 'ESPECES' && '💵'}
                          {mode === 'CHEQUE' && '📝'}
                          {mode === 'VIREMENT' && '🏦'}
                          {mode === 'CARTE_BANCAIRE' && '💳'}
                          {mode === 'TRAITE' && '📄'}
                          {mode === 'AUTRE' && '❓'}
                        </span>
                        {t(`reglement.${mode.toLowerCase()}`)}
                      </td>
                      <td className="text-center">{data.count}</td>
                      <td className="text-end">{formatPrice(data.montant)}</td>
                      <td className="text-center">
                        {totalMontant > 0 ? Math.round((data.montant / totalMontant) * 100) : 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>

        {/* Répartition par statut */}
        <Col md={6}>
          <Card className="shadow-sm mb-4">
            <CardHeader>
              <h5 className="mb-0">{t('reglement.repartitionStatuts')}</h5>
            </CardHeader>
            <CardBody>
              <Table striped size="sm">
                <thead>
                  <tr>
                    <th>{t('reglement.statut')}</th>
                    <th className="text-center">{t('common.count')}</th>
                    <th className="text-end">{t('reglement.montantTotal')}</th>
                    <th className="text-center">%</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(repartitionStatuts).map(([statut, data]) => {
                    const badgeColors = {
                      'EN_ATTENTE': 'warning',
                      'VALIDE': 'info',
                      'ENCAISSE': 'success',
                      'REJETE': 'danger',
                      'ANNULE': 'secondary'
                    };
                    
                    return (
                      <tr key={statut}>
                        <td>
                          <Badge color={badgeColors[statut] || 'secondary'}>
                            {t(`reglement.${statut.toLowerCase()}`)}
                          </Badge>
                        </td>
                        <td className="text-center">{data.count}</td>
                        <td className="text-end">{formatPrice(data.montant)}</td>
                        <td className="text-center">
                          {totalMontant > 0 ? Math.round((data.montant / totalMontant) * 100) : 0}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Liste détaillée */}
      <Card className="shadow-sm">
        <CardHeader>
          <h5 className="mb-0">{t('reglement.listeDetaillee')}</h5>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <div className="mt-2 text-muted">{t('common.loading')}</div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead className="thead-light">
                  <tr>
                    <th>{t('reglement.reference')}</th>
                    <th>{t('reglement.client')}</th>
                    <th>{t('reglement.dateReglement')}</th>
                    <th className="text-end">{t('reglement.montantRegle')}</th>
                    <th>{t('reglement.modeReglement')}</th>
                    <th>{t('reglement.statut')}</th>
                    <th>{t('reglement.documents')}</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((reglement) => {
                    const client = clients.find(c => c.id === reglement.clientId);
                    return (
                      <tr key={reglement.id}>
                        <td>
                          <strong>{reglement.reference || reglement.id}</strong>
                          {reglement.referencePaiement && (
                            <div className="text-muted small">
                              {reglement.referencePaiement}
                            </div>
                          )}
                        </td>
                        <td>
                          {client ? `${client.nom} ${client.prenom || ''}`.trim() : 'N/A'}
                        </td>
                        <td>
                          {new Date(reglement.dateReglement).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="text-end">
                          <strong>{formatPrice(reglement.montantRegle)}</strong>
                        </td>
                        <td>
                          <span className="me-1">
                            {reglement.modeReglement === 'ESPECES' && '💵'}
                            {reglement.modeReglement === 'CHEQUE' && '📝'}
                            {reglement.modeReglement === 'VIREMENT' && '🏦'}
                            {reglement.modeReglement === 'CARTE_BANCAIRE' && '💳'}
                            {reglement.modeReglement === 'TRAITE' && '📄'}
                            {reglement.modeReglement === 'AUTRE' && '❓'}
                          </span>
                          {t(`reglement.${reglement.modeReglement.toLowerCase()}`)}
                        </td>
                        <td>
                          <Badge color={
                            reglement.statut === 'EN_ATTENTE' ? 'warning' :
                            reglement.statut === 'VALIDE' ? 'info' :
                            reglement.statut === 'ENCAISSE' ? 'success' :
                            reglement.statut === 'REJETE' ? 'danger' : 'secondary'
                          }>
                            {t(`reglement.${reglement.statut.toLowerCase()}`)}
                          </Badge>
                        </td>
                        <td>
                          {reglement.numFactures && (
                            <div className="mb-1">
                              <small className="text-primary">
                                F: {reglement.numFactures}
                              </small>
                            </div>
                          )}
                          {reglement.numBonLivraisons && (
                            <div>
                              <small className="text-success">
                                BL: {reglement.numBonLivraisons}
                              </small>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {reportData.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <div className="text-muted">{t('reglement.noData')}</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default ReglementReport;

